import React, { useCallback, useState } from "react";
import { useTicketViewing } from "../../../hooks/comms/useTicketViewing";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import TicketActionModal from "./TicketActionModal";
import MergeTicketDialog from "./MergeTicketDialog";
import { ViewingStrip } from "./ViewingStrip";
import { extractTicketDetail, getTicketClientName } from "./ticketUtils";
import { useTicketDetail } from "./useTicketDetail";
import { useTicketActions } from "./useTicketActions";
import { useAgentsWorkload } from "./useSupportVocabulary";

/**
 * Everything the ticket surface needs to open one ticket: its detail, the full
 * v1.9 action set, the action modal those actions read their input from, and
 * the escalation roster.
 *
 * Kept separate from the layout so the desktop pane and the mobile drawer wire
 * the identical detail → action → optimistic refresh loop.
 *
 * @param {string|null} roomId - Selected ticket room id.
 * @param {string|number|null} [viewerUserId] - The current agent, for the
 *   collision-detection heartbeat.
 * @param {() => void} [onChanged] - Called after any successful action, for
 *   callers that also need to refresh something outside the ticket (the queue).
 * @returns {{
 *   detail: Object|null,
 *   loading: boolean,
 *   error: string|null,
 *   reload: () => void,
 *   agents: Array<{ userId: string, name: string }>,
 *   centerProps: Object,
 *   actionModal: React.ReactNode,
 *   toast: { message: string, type: string, id: number }|null,
 * }}
 */
export function useTicketWorkspace(roomId, viewerUserId = null, onChanged) {
  const [pendingAction, setPendingAction] = useState(null); // escalate|snooze|merge|unassign
  const { getCoachName } = useCommsIdentity();
  // Only the escalate picker needs the roster, so it isn't fetched until an
  // action modal is actually opened — and then from the shared cache, which
  // the views rail has usually already filled.
  const { agents } = useAgentsWorkload(Boolean(pendingAction));

  // Who else has this ticket open right now.
  const { viewers } = useTicketViewing(roomId, viewerUserId);

  const { detail, loading, error, reload, applyLocal } = useTicketDetail(roomId);

  // Actions may hand back an optimistic partial (pick-up → new assignee);
  // apply it before the refetch so the header updates instantly.
  const handleAfterAction = useCallback(
    (optimisticPartial) => {
      if (optimisticPartial) {
        applyLocal(optimisticPartial);
      }
      reload();
      onChanged?.();
    },
    [reload, applyLocal, onChanged]
  );

  const {
    toast,
    busy,
    handleEscalate,
    handleResolve,
    handleReopen,
    handlePickUp,
    handleUnassign,
    handleSnooze,
    handleUnsnooze,
    handleMerge,
    handlePatch,
  } = useTicketActions(roomId, handleAfterAction);

  /**
   * Route the modal's payload to the action the agent picked. Merge has its own
   * dialog — picking a ticket by room id out of a text box was never usable.
   */
  const handleActionSubmit = useCallback(
    (payload) => {
      switch (pendingAction) {
        case "escalate":
          return handleEscalate(payload);
        case "snooze":
          return handleSnooze(payload);
        case "unassign":
          return handleUnassign(payload);
        default:
          return Promise.resolve();
      }
    },
    [pendingAction, handleEscalate, handleSnooze, handleUnassign]
  );

  const ticket = extractTicketDetail(detail) || {};
  const clientId = ticket.client_id || detail?.client?.id || null;

  // The event may not carry a name — fall back to the shared identity cache,
  // then to a generic label rather than showing a raw user id.
  const namedViewers = viewers.map((v) => ({
    ...v,
    name: v.name || getCoachName(v.userId) || null,
  }));

  const centerProps = {
    roomId,
    detail,
    viewerUserId,
    banner: <ViewingStrip viewers={namedViewers} />,
    onRequestAction: setPendingAction,
    onResolve: handleResolve,
    onReopen: handleReopen,
    onUnsnooze: handleUnsnooze,
    onPatch: handlePatch,
    onPickUp: handlePickUp,
    busy,
  };

  const actionModal = (
    <>
      <TicketActionModal
        action={pendingAction === "merge" ? null : pendingAction}
        onClose={() => setPendingAction(null)}
        onSubmit={handleActionSubmit}
        agents={agents}
      />
      {pendingAction === "merge" && roomId && (
        <MergeTicketDialog
          ticket={ticket}
          clientId={clientId}
          clientName={getTicketClientName(ticket) || "This requester"}
          onClose={() => setPendingAction(null)}
          onMerge={handleMerge}
        />
      )}
    </>
  );

  return {
    detail,
    loading,
    error,
    reload,
    agents,
    viewers: namedViewers,
    centerProps,
    actionModal,
    toast,
  };
}
